import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export interface PdfTemplate {
  id: string;
  name: string;
  description?: string;
  templateType: string;
  content: any;
  isDefault: boolean;
  isActive: boolean;
  buildingId?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    name: string;
    email: string;
  };
  building?: {
    name: string;
  };
  operatorId?: string | null;
  operator?: {
    name: string;
  };
}

export type PdfTemplateResolutionSource = 'building' | 'operator' | 'global';

export interface ResolvedPdfTemplate {
  source: PdfTemplateResolutionSource;
  template: PdfTemplate;
  buildingTemplateId: string | null;
  operatorTemplateId: string | null;
  systemTemplateId: string | null;
  buildingName: string | null;
}

export interface CreatePdfTemplateDto {
  name: string;
  description?: string;
  templateType: string;
  content: any;
  isDefault?: boolean;
  isActive?: boolean;
  buildingId?: string;
  operatorId?: string;
}

export interface UpdatePdfTemplateDto extends Partial<CreatePdfTemplateDto> {}

export interface SeedDefaultTemplatesResponse {
  message: string;
  templates: PdfTemplate[];
}

class PdfTemplatesApi {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async resolveForBuilding(
    buildingId: string,
    templateType = 'fee_notice',
  ): Promise<ResolvedPdfTemplate> {
    const response = await axios.get(`${API_BASE_URL}/pdf-templates/resolve`, {
      headers: this.getAuthHeaders(),
      params: { buildingId, templateType },
    });
    return response.data;
  }

  async upsertBuildingFeeNotice(
    buildingId: string,
    content: Record<string, unknown>,
  ): Promise<PdfTemplate> {
    const response = await axios.post(
      `${API_BASE_URL}/pdf-templates/building/${buildingId}/fee-notice`,
      { content },
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async resetBuildingFeeNotice(buildingId: string): Promise<{ deleted: boolean; message: string }> {
    const response = await axios.delete(
      `${API_BASE_URL}/pdf-templates/building/${buildingId}/fee-notice`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async upsertBuildingDefault(
    buildingId: string,
    payload: {
      templateType: string;
      content: Record<string, unknown>;
      name?: string;
      description?: string;
    },
  ): Promise<PdfTemplate> {
    const response = await axios.post(
      `${API_BASE_URL}/pdf-templates/building/${buildingId}/default`,
      payload,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async resetBuildingDefault(
    buildingId: string,
    templateType: string,
  ): Promise<{ deleted: boolean; message: string }> {
    const response = await axios.delete(
      `${API_BASE_URL}/pdf-templates/building/${buildingId}/default`,
      {
        headers: this.getAuthHeaders(),
        params: { templateType },
      },
    );
    return response.data;
  }

  async getTemplates(): Promise<PdfTemplate[]> {
    const response = await axios.get(`${API_BASE_URL}/pdf-templates`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getTemplate(id: string): Promise<PdfTemplate> {
    const response = await axios.get(`${API_BASE_URL}/pdf-templates/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createTemplate(template: CreatePdfTemplateDto): Promise<PdfTemplate> {
    const response = await axios.post(`${API_BASE_URL}/pdf-templates`, template, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateTemplate(id: string, template: UpdatePdfTemplateDto): Promise<PdfTemplate> {
    const response = await axios.patch(`${API_BASE_URL}/pdf-templates/${id}`, template, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async setAsDefault(id: string): Promise<PdfTemplate> {
    const response = await axios.patch(`${API_BASE_URL}/pdf-templates/${id}/set-default`, {}, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteTemplate(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/pdf-templates/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async seedDefaultTemplates(): Promise<SeedDefaultTemplatesResponse> {
    const response = await axios.post(`${API_BASE_URL}/pdf-templates/seed-defaults`, {}, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async repairOperatorScopes(operatorId?: string): Promise<{ updatedCount: number }> {
    const response = await axios.post(
      `${API_BASE_URL}/pdf-templates/repair-operator-scopes`,
      {},
      {
        headers: this.getAuthHeaders(),
        params: operatorId ? { operatorId } : undefined,
      },
    );
    return response.data;
  }

  async previewTemplate(templateId: string): Promise<Blob> {
    const response = await axios.get(`${API_BASE_URL}/pdf-templates/${templateId}/preview`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    });
    const contentType =
      (response.headers['content-type'] as string) || 'application/pdf';
    return new Blob([response.data], { type: contentType });
  }
}

export const pdfTemplatesApi = new PdfTemplatesApi(); 